import type { FileIconType, MailItem, MeetingNoteItem, MessageItem, VaultFileItem, VaultFolderNode } from "@/types/vault.types";

const mimeMap: Record<FileIconType, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  image: "image/png",
  link: "text/html",
  text: "text/plain",
};

const extMap: Record<FileIconType, string> = {
  pdf: ".pdf",
  docx: ".docx",
  xlsx: ".xlsx",
  image: ".png",
  link: "",
  text: ".txt",
};

function mockFile(id: string, name: string, fileIcon: FileIconType, date: string): VaultFileItem {
  return {
    id,
    name,
    fileIcon,
    date,
    sizeBytes: 0,
    sizeFormatted: "—",
    fileType: mimeMap[fileIcon] || "application/octet-stream",
    extension: extMap[fileIcon] || "",
    createdAt: "",
    updatedAt: "",
    createdAtFormatted: "",
    updatedAtFormatted: date,
  };
}

export const vaultMailItems: MailItem[] = [
  { id: "dj-m1", subject: "RE: Recording Deal - Territory Rights Update", from: "Kecia Tatum (UMG Legal)", preview: "Following our call, I've updated Section 3.4 to clarify the international distribution rights. Please review the redline...", date: "Mar 15, 2026", hasAttachment: true, unread: true },
  { id: "dj-m2", subject: "A&R Recap - Spring Signings", from: "Nicole Billings (A&R)", preview: "Quick recap from today's A&R meeting. We're moving forward with two new artist signings for Q2. Budget approval needed...", date: "Mar 14, 2026", hasAttachment: false, unread: true },
  { id: "dj-m3", subject: "Sync Approval - Apple TV+ Series", from: "Jeff Harleston (Legal Affairs)", preview: "The sync team has cleared the use of 'Champion Sound' for the Apple TV+ series. Attached is the executed license agreement...", date: "Mar 12, 2026", hasAttachment: true, unread: false },
  { id: "dj-m4", subject: "Q1 Royalty Statements - Final Review", from: "Finance Team (UMG)", preview: "All Q1 2026 royalty statements are ready for final approval. Please review the artist breakdowns before we send...", date: "Mar 10, 2026", hasAttachment: true, unread: false },
  { id: "dj-m5", subject: "Recording Session Budget - Studio A", from: "Production Coordinator", preview: "The budget for next week's sessions at Studio A has been finalized. Total studio time: 40 hours across three days...", date: "Mar 8, 2026", hasAttachment: true, unread: false },
  { id: "dj-m6", subject: "Label Copy - New Releases March", from: "Marketing (Def Jam)", preview: "Here's the label copy for the March releases. Please confirm the metadata and ensure all publishing credits are accurate...", date: "Mar 5, 2026", hasAttachment: false, unread: false },
];

export const vaultMeetingNotes: MeetingNoteItem[] = [
  { id: "dj-mt1", title: "A&R Review - New Artist Pipeline", date: "Mar 14, 2026", duration: "60 min", attendees: ["Nicole Billings", "A&R Team", "You"], source: "Zoom" },
  { id: "dj-mt2", title: "Legal Affairs Weekly - Contract Status", date: "Mar 12, 2026", duration: "45 min", attendees: ["Jeff Harleston", "Legal Team", "You"], source: "Google Meet" },
  { id: "dj-mt3", title: "Artist Development - Q2 Planning", date: "Mar 10, 2026", duration: "75 min", attendees: ["Kecia Tatum", "Development Team", "You"], source: "Teams" },
  { id: "dj-mt4", title: "Sync Licensing Strategy Session", date: "Mar 7, 2026", duration: "50 min", attendees: ["Sync Team", "You"], source: "Zoom" },
  { id: "dj-mt5", title: "Finance Review - Royalty Processing", date: "Mar 4, 2026", duration: "30 min", attendees: ["Finance Team", "You"], source: "Google Meet" },
];

export const vaultMessages: MessageItem[] = [
  { id: "dj-ms1", channel: "#deal-coco-recording", sender: "Kecia Tatum", preview: "Just finalized the touring provisions. Ready for your review.", date: "Mar 15, 2026", source: "Slack" },
  { id: "dj-ms2", channel: "#legal-review", sender: "Jeff Harleston", preview: "Can someone review the Apple sync agreement before 3pm?", date: "Mar 14, 2026", source: "Slack" },
  { id: "dj-ms3", channel: "#sync-licensing", sender: "Sync Team", preview: "Three new sync requests came in today - all major placements", date: "Mar 12, 2026", source: "Slack" },
  { id: "dj-ms4", channel: "#a-r-pipeline", sender: "Nicole Billings", preview: "Adding two new artists to the review queue for next week", date: "Mar 10, 2026", source: "Teams" },
  { id: "dj-ms5", channel: "#general", sender: "Admin Bot", preview: "Reminder: Q1 financial close is March 20. All expenses must be submitted.", date: "Mar 8, 2026", source: "Slack" },
];

export const vaultEqualiqRoot: VaultFolderNode = {
  id: "dj-eq-root",
  name: "EQUAL-IQ",
  children: [
    {
      id: "dj-eq-templates",
      name: "Templates",
      children: [
        mockFile("dj-tf1", "Recording Deal Template - Standard.docx", "docx", "Jan 15, 2026"),
        mockFile("dj-tf2", "Recording Agreement - Major Label.docx", "docx", "Jan 10, 2026"),
        mockFile("dj-tf3", "Sync License Template.docx", "docx", "Jan 5, 2026"),
        mockFile("dj-tf4", "Distribution Agreement Template.docx", "docx", "Dec 20, 2025"),
        mockFile("dj-tf5", "Artist Development Budget.xlsx", "xlsx", "Dec 15, 2025"),
      ],
    },
    {
      id: "dj-eq-artist-agreements",
      name: "Artist Agreements",
      children: [
        {
          id: "dj-eq-artist-coco",
          name: "Coco Jones",
          children: [
            mockFile("dj-af1", "Recording Deal - Executed.pdf", "pdf", "Mar 1, 2026"),
            mockFile("dj-af2", "Touring Addendum.pdf", "pdf", "Feb 25, 2026"),
            mockFile("dj-af3", "Merchandising Rights.pdf", "pdf", "Feb 20, 2026"),
          ],
        },
        {
          id: "dj-eq-artist-bieber",
          name: "Justin Bieber",
          children: [
            mockFile("dj-af4", "Recording Agreement - Amendment 3.pdf", "pdf", "Feb 15, 2026"),
            mockFile("dj-af5", "Publishing Administration.pdf", "pdf", "Feb 10, 2026"),
            mockFile("dj-af6", "Label Services Agreement.pdf", "pdf", "Jan 20, 2026"),
            mockFile("dj-af7", "Distribution Terms.pdf", "pdf", "Jan 15, 2026"),
          ],
        },
      ],
    },
    {
      id: "dj-eq-sync",
      name: "Sync Licenses",
      children: [
        mockFile("dj-sf1", "Apple TV+ - Champion Sound.pdf", "pdf", "Mar 12, 2026"),
        mockFile("dj-sf2", "Nike Commercial - Q2 2026.pdf", "pdf", "Mar 5, 2026"),
        mockFile("dj-sf3", "Netflix Film - Catalog Use.pdf", "pdf", "Feb 28, 2026"),
        mockFile("dj-sf4", "EA Sports - Video Game License.pdf", "pdf", "Feb 20, 2026"),
      ],
    },
    {
      id: "dj-eq-legal",
      name: "Legal",
      children: [
        {
          id: "dj-eq-legal-ndas",
          name: "NDAs",
          children: [
            mockFile("dj-lf1", "NDA - UMG Internal.pdf", "pdf", "Feb 1, 2026"),
            mockFile("dj-lf2", "NDA - Apple Music.pdf", "pdf", "Jan 25, 2026"),
            mockFile("dj-lf3", "NDA - Production Partners.pdf", "pdf", "Jan 15, 2026"),
          ],
        },
        mockFile("dj-lf4", "Standard Terms - Artist Agreements.pdf", "pdf", "Jan 10, 2026"),
        mockFile("dj-lf5", "https://defjam.com/legal/policies", "link", "Jan 1, 2026"),
      ],
    },
    {
      id: "dj-eq-financial",
      name: "Financial",
      children: [
        mockFile("dj-ff1", "Q1 2026 Royalty Statements - All Artists.xlsx", "xlsx", "Mar 10, 2026"),
        mockFile("dj-ff2", "Recording Budget Tracker - Q1.xlsx", "xlsx", "Mar 1, 2026"),
        mockFile("dj-ff3", "Marketing Spend Analysis.xlsx", "xlsx", "Feb 25, 2026"),
        mockFile("dj-ff4", "Advance Recoupment Status.xlsx", "xlsx", "Feb 15, 2026"),
      ],
    },
  ],
};

export const vaultGdriveRoot: VaultFolderNode = {
  id: "dj-gd-root",
  name: "Google Drive",
  children: [
    {
      id: "dj-gd-contracts",
      name: "Contracts",
      children: [
        {
          id: "dj-gd-contracts-active",
          name: "Active",
          children: [
            mockFile("dj-gd1", "Contract Negotiation Notes - Coco Jones.docx", "docx", "Mar 14, 2026"),
            mockFile("dj-gd2", "Deal Terms Comparison Matrix.xlsx", "xlsx", "Mar 10, 2026"),
          ],
        },
        {
          id: "dj-gd-contracts-archive",
          name: "Archive",
          children: [
            mockFile("dj-gd3", "2025 Deal Archive - Summary.xlsx", "xlsx", "Jan 5, 2026"),
          ],
        },
      ],
    },
    {
      id: "dj-gd-reports",
      name: "Reports",
      children: [
        mockFile("dj-gd4", "Artist Roster - March 2026.pdf", "pdf", "Mar 15, 2026"),
        mockFile("dj-gd5", "Revenue Forecast - Q2 2026.xlsx", "xlsx", "Mar 8, 2026"),
        mockFile("dj-gd6", "Marketing Performance Analysis.pdf", "pdf", "Mar 1, 2026"),
      ],
    },
  ],
};

export const vaultDropboxRoot: VaultFolderNode = {
  id: "dj-db-root",
  name: "Dropbox",
  children: [
    {
      id: "dj-db-sessions",
      name: "Sessions",
      children: [
        mockFile("dj-db1", "Studio A - March Schedule.pdf", "pdf", "Mar 12, 2026"),
        mockFile("dj-db2", "Master Recording Archive - Q1.pdf", "pdf", "Mar 5, 2026"),
      ],
    },
    {
      id: "dj-db-artwork",
      name: "Artwork",
      children: [
        mockFile("dj-db3", "Album Artwork - Spring Releases.pdf", "image", "Mar 10, 2026"),
        mockFile("dj-db4", "Press Kit Assets.pdf", "image", "Mar 3, 2026"),
      ],
    },
    mockFile("dj-db5", "Historical Agreements Archive.pdf", "pdf", "Feb 15, 2026"),
  ],
};

export const vaultDealFilesMap: Record<string, VaultFileItem[]> = {
  "dj-deal-1": [
    mockFile("dj-f1", "Recording Deal Agreement - Executed.pdf", "pdf", "Mar 1, 2026"),
    mockFile("dj-f2", "Recording Commitment Schedule.pdf", "pdf", "Feb 28, 2026"),
    mockFile("dj-f3", "Touring & Live Performance Rights.pdf", "pdf", "Feb 25, 2026"),
    mockFile("dj-f4", "Merchandising Agreement.pdf", "pdf", "Feb 22, 2026"),
    mockFile("dj-f5", "Royalty Structure Breakdown.xlsx", "xlsx", "Feb 20, 2026"),
    mockFile("dj-f6", "Territory Rights Exhibit.pdf", "pdf", "Feb 18, 2026"),
  ],
  "dj-deal-2": [
    mockFile("dj-f7", "Recording Agreement - Major Release.pdf", "pdf", "Feb 15, 2026"),
    mockFile("dj-f8", "Advance Payment Schedule.xlsx", "xlsx", "Feb 12, 2026"),
    mockFile("dj-f9", "Marketing Budget Commitment.pdf", "pdf", "Feb 10, 2026"),
    mockFile("dj-f10", "https://defjam.com/artist-portal", "link", "Feb 5, 2026"),
  ],
  "dj-deal-3": [
    mockFile("dj-f11", "Distribution Agreement - Digital.pdf", "pdf", "Feb 8, 2026"),
    mockFile("dj-f12", "Revenue Split Terms.xlsx", "xlsx", "Feb 5, 2026"),
    mockFile("dj-f13", "Platform Performance Metrics.pdf", "pdf", "Feb 1, 2026"),
  ],
  "dj-deal-4": [
    mockFile("dj-f14", "Sync License - Apple TV+.pdf", "pdf", "Mar 12, 2026"),
    mockFile("dj-f15", "Usage Rights & Restrictions.pdf", "pdf", "Mar 10, 2026"),
    mockFile("dj-f16", "Licensing Fee Schedule.xlsx", "xlsx", "Mar 8, 2026"),
  ],
  "dj-deal-5": [
    mockFile("dj-f17", "Publishing Administration Agreement.pdf", "pdf", "Jan 25, 2026"),
    mockFile("dj-f18", "Song Catalog - Registered Works.xlsx", "xlsx", "Jan 22, 2026"),
    mockFile("dj-f19", "Collection Territory Terms.pdf", "pdf", "Jan 20, 2026"),
  ],
  "dj-deal-6": [
    mockFile("dj-f20", "Featured Artist Collaboration.pdf", "pdf", "Feb 18, 2026"),
    mockFile("dj-f21", "Split Sheet - Writing Credits.xlsx", "xlsx", "Feb 15, 2026"),
    mockFile("dj-f22", "Recording & Distribution Terms.pdf", "pdf", "Feb 12, 2026"),
    mockFile("dj-f23", "NDA - Collaboration Project.pdf", "pdf", "Feb 8, 2026"),
  ],
  "dj-deal-7": [
    mockFile("dj-f24", "Label Services Agreement - Dreamville.pdf", "pdf", "Jan 20, 2026"),
    mockFile("dj-f25", "Distribution & Marketing Support.pdf", "pdf", "Jan 18, 2026"),
    mockFile("dj-f26", "Revenue Share Model.xlsx", "xlsx", "Jan 15, 2026"),
    mockFile("dj-f27", "https://dreamville.com/partnership", "link", "Jan 10, 2026"),
  ],
  "dj-deal-8": [
    mockFile("dj-f28", "Merchandise Licensing Agreement.pdf", "pdf", "Mar 5, 2026"),
    mockFile("dj-f29", "Product Line Catalog.xlsx", "xlsx", "Mar 3, 2026"),
    mockFile("dj-f30", "Brand Guidelines & Usage.pdf", "pdf", "Mar 1, 2026"),
    mockFile("dj-f31", "Revenue Projections - Q2 2026.xlsx", "xlsx", "Feb 28, 2026"),
    mockFile("dj-f32", "Vendor Partnership Terms.pdf", "pdf", "Feb 25, 2026"),
  ],
  "dj-deal-9": [
    mockFile("dj-f33", "Touring Agreement - Summer 2026.pdf", "pdf", "Feb 22, 2026"),
    mockFile("dj-f34", "Venue Contracts - Multi-City.pdf", "pdf", "Feb 20, 2026"),
    mockFile("dj-f35", "Production Budget & Rider.xlsx", "xlsx", "Feb 18, 2026"),
    mockFile("dj-f36", "Insurance & Liability Coverage.pdf", "pdf", "Feb 15, 2026"),
  ],
  "dj-deal-11": [
    mockFile("dj-f41", "Remix & Production Agreement.pdf", "pdf", "Feb 10, 2026"),
    mockFile("dj-f42", "Producer Credit & Royalty Terms.xlsx", "xlsx", "Feb 8, 2026"),
    mockFile("dj-f43", "Master Recording Rights.pdf", "pdf", "Feb 5, 2026"),
  ],
  "dj-deal-12": [
    mockFile("dj-f44", "Video Game Licensing - EA Sports.pdf", "pdf", "Feb 20, 2026"),
    mockFile("dj-f45", "Multi-Track Usage License.pdf", "pdf", "Feb 18, 2026"),
    mockFile("dj-f46", "Licensing Fee & Royalty Terms.xlsx", "xlsx", "Feb 15, 2026"),
    mockFile("dj-f47", "Territory & Platform Rights.pdf", "pdf", "Feb 12, 2026"),
  ],
};
